import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ArrowRight, Hash, User as UserIcon, CheckSquare, Sparkles } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useCommandPaletteOpen, closeCommandPalette } from "@/lib/uiState";
import { useRooms } from "@/lib/rooms";
import { useMembers } from "@/lib/workspace";
import { useQuery } from "@tanstack/react-query";
import { taskApi } from "@/features/tasks/api";
import { cn } from "@/lib/cn";

type Item = {
  id: string;
  type: "room" | "person" | "task" | "command";
  label: string;
  hint?: string;
  to?: string;
  action?: () => void;
};

export function CommandPalette(): JSX.Element {
  const [open] = useCommandPaletteOpen();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const { rooms: availableRooms } = useRooms();
  const { members } = useMembers();
  const taskQuery = useQuery({ queryKey: ["palette-tasks"], enabled: open, queryFn: () => taskApi.listTasks() });
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      // small delay so focus wins after Modal mounts
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const q = query.trim().toLowerCase();
    const matches = (s: string) => !q || s.toLowerCase().includes(q);
    const rooms: Item[] = availableRooms.filter((r) => matches(r.name)).map((r) => ({
      id: r.id,
      type: "room",
      label: r.name,
      hint: r.kind === "dm" ? t("nav.dm") : t("nav.rooms"),
      to: `/rooms/${r.id}`,
    }));
    const people: Item[] = members.filter((u) => matches(u.displayName) || matches(u.email)).map(
      (u) => ({
        id: u.id,
        type: "person",
        label: u.displayName,
        hint: u.email,
        to: `/settings/members?q=${encodeURIComponent(u.email)}`,
      }),
    );
    const tasks: Item[] = (taskQuery.data?.tasks || []).filter((task) => matches(task.title))
      .slice(0, 5)
      .map((task) => ({
        id: task.id,
        type: "task",
        label: task.title,
        hint: t("nav.tasks"),
        to: `/tasks#${task.id}`,
      }));
    const commands: Item[] = (
      [
        {
          id: "cmd:home",
          type: "command",
          label: t("nav.home"),
          hint: t("search.command"),
          to: "/",
        },
        {
          id: "cmd:tasks",
          type: "command",
          label: t("nav.tasks"),
          hint: t("search.command"),
          to: "/tasks",
        },
        {
          id: "cmd:files",
          type: "command",
          label: t("nav.files"),
          hint: t("search.command"),
          to: "/files",
        },
        {
          id: "cmd:settings",
          type: "command",
          label: t("nav.settings"),
          hint: t("search.command"),
          to: "/settings",
        },
        {
          id: "cmd:lang",
          type: "command",
          label: i18n.language?.startsWith("ja")
            ? "Switch to English"
            : "日本語に切り替える",
          hint: t("settings.language"),
          action: () => {
            const next = i18n.language?.startsWith("ja") ? "en-US" : "ja-JP";
            void i18n.changeLanguage(next);
          },
        },
      ] satisfies Item[]
    ).filter((c) => matches(c.label));
    return [...commands, ...rooms, ...people, ...tasks, ...(q ? [{ id: "search-all", type: "command" as const, label: `「${query}」を全体検索`, to: `/search?q=${encodeURIComponent(query)}` }] : [])];
  }, [query, t, i18n, availableRooms, members, taskQuery.data]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const choose = (item: Item) => {
    if (item.action) item.action();
    if (item.to) navigate(item.to);
    closeCommandPalette();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[active];
      if (item) choose(item);
    }
  };

  return (
    <Modal
      open={open}
      onClose={closeCommandPalette}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <Sparkles size={18} aria-hidden className="text-accent-ai" />
          {t("search.placeholder")}
        </span>
      }
    >
      <div onKeyDown={onKey}>
        <div className="flex items-center gap-2 border border-border-default rounded-md px-3 h-10 mb-3 focus-within:shadow-focus">
          <Search size={16} aria-hidden className="text-fg-muted" />
          <input
            ref={inputRef}
            data-autofocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search.placeholder")}
            aria-label={t("search.placeholder")}
            className="flex-1 outline-none text-base bg-transparent"
          />
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-fg-muted py-6 text-center">{t("search.noResults")}</p>
        ) : (
          <ul role="listbox" className="max-h-80 overflow-auto -mx-2">
            {items.map((item, i) => (
              <li key={item.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(item)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 rounded-md text-left",
                    i === active ? "bg-brand-100 text-brand-700" : "hover:bg-surface-raised",
                  )}
                >
                  <ItemIcon type={item.type} />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.hint ? (
                    <span className="text-xs text-fg-muted">{item.hint}</span>
                  ) : null}
                  <ArrowRight size={14} aria-hidden className="text-fg-muted" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function ItemIcon({ type }: { type: Item["type"] }): JSX.Element {
  switch (type) {
    case "room":
      return <Hash size={14} aria-hidden className="text-fg-muted" />;
    case "person":
      return <UserIcon size={14} aria-hidden className="text-fg-muted" />;
    case "task":
      return <CheckSquare size={14} aria-hidden className="text-accent-task" />;
    case "command":
    default:
      return <Sparkles size={14} aria-hidden className="text-accent-ai" />;
  }
}
